import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  linkClassName,
  secondaryTextClassName,
  useAuth,
  type AuthFormError,
} from '@monecolier/ui';
import { forgotPassword } from '@monecolier/api-client';

// AUT-02 : `apiClient` réutilisé tel quel depuis le contexte — même raison que
// Login.tsx/Register.tsx (l'instance injectée dans <AuthProvider>, réelle en prod,
// mockée en test), même si cette page n'utilise aucune méthode d'`useAuth()` liée à
// une session (pas de login/register ici : POST /auth/forgot-password ne modifie
// jamais l'état d'authentification, appelé directement via `apiClient`).
export function ForgotPassword() {
  const { apiClient } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<AuthFormError | null>(null);
  // Message renvoyé par le backend, TOUJOURS le même que le compte existe ou non
  // (anti-énumération, AUT-02) — affiché tel quel dès réception, jamais reformulé ni
  // conditionné à un quelconque statut "trouvé/non trouvé" côté client : il n'y en a
  // qu'UN côté backend, recréer une distinction ici recréerait une fuite d'info que
  // le backend s'est donné la peine d'éliminer.
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await forgotPassword(apiClient, { email });
      setConfirmationMessage(result.message);
    } catch (err) {
      // Un échec ICI (network/CORS — voir authFormError.ts) est un vrai problème de
      // connectivité/configuration, PAS un signal sur l'existence du compte : le
      // backend ne renvoie jamais d'erreur liée à l'anti-énumération elle-même
      // (toujours 200), donc tout ce qui atteint ce catch est une panne réelle.
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (confirmationMessage) {
    return (
      <Card title={t('forgotPassword.title')}>
        <p role="status" data-testid="forgot-password-confirmation" className="text-sm text-brand-700">
          {confirmationMessage}
        </p>
        <p className={secondaryTextClassName}>
          <Link to="/login" className={linkClassName}>
            {t('forgotPassword.backToLogin')}
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <Card title={t('forgotPassword.title')}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className={formFieldClassName}>
          {t('forgotPassword.email')}
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
            className={formInputClassName}
          />
        </label>
        {error ? (
          <p role="alert" data-testid="forgot-password-error" className={formErrorClassName(error.kind)}>
            {error.message}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
        </Button>
      </form>
      <p className={secondaryTextClassName}>
        <Link to="/login" className={linkClassName}>
          {t('forgotPassword.backToLogin')}
        </Link>
      </p>
    </Card>
  );
}
